import { useCallback, useEffect, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";

import { Loader2 } from "lucide-react";

import firestoreService from "../../services/firestoreService";
import usePageTracking from "../../components/hooks/usePageTracking";

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../../components/ui/breadcrumb";
import { Separator } from "../../components/ui/separator";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "../../components/ui/sidebar";

import AppSidebar from "./AppSidebar";

const AppLayout = () => {
  const [fetchingProject, setFetchingProject] = useState(false);
  const [projectId, setProjectId] = useState("");
  const [projectName, setProjectName] = useState("");
  const [fetchingHazard, setFetchingHazard] = useState(false);
  const [hazardIdentifier, setHazardIdentifier] = useState("");
  const [locationTokens, setLocationTokens] = useState([]);

  const location = useLocation();
  
  // Track page views as users navigate through the application
  usePageTracking();

  const fetchProjectName = useCallback(async (projectId) => {
    try {
      setFetchingProject(true);

      const project = await firestoreService.get("projects", projectId);

      if (project) {
        setProjectName(project.name);
      }
    } finally {
      setFetchingProject(false);
    }
  }, []);

  const fetchHazardName = useCallback(async (projectId, hazardId) => {
    try {
      setFetchingHazard(true);

      const hazard = await firestoreService.get(
        `projects/${projectId}/hazards`,
        hazardId
      );

      if (hazard) {
        setHazardIdentifier(hazard.hazardId);
      }
    } finally {
      setFetchingHazard(false);
    }
  }, []);

  useEffect(() => {
    const tokens = location.pathname.split("/");
    tokens.shift();

    setLocationTokens(tokens);

    // Projects
    if (tokens[0] === "projects" && tokens.length > 2) {
      const projectId = tokens[1];
      setProjectId(projectId);

      if (projectId) {
        fetchProjectName(projectId);
      }

      // Hazards
      if (tokens[2] === "hazards" && tokens.length > 4) {
        const hazardId = tokens[3];

        if (hazardId) {
          fetchHazardName(projectId, hazardId);
        }
      }
    }
  }, [location, fetchProjectName, fetchHazardName]);

  return (
    <div className="[--header-height:calc(theme(spacing.14))]">
      <SidebarProvider>
        <AppSidebar />

        <SidebarInset>
          <header className="flex sticky top-0 z-50 w-full items-center border-b bg-background">
            <div className="flex h-[--header-height] w-full items-center gap-2 px-4">
              <SidebarTrigger className="-ml-1" />

              <Separator orientation="vertical" className="mr-2 h-4" />

              <Breadcrumb>
                <BreadcrumbList>
                  {/* Projects */}
                  {(location.pathname === "/" ||
                    locationTokens[0] === "projects") && (
                    <>
                      <Link to="/projects">
                        <BreadcrumbItem>
                          <BreadcrumbPage>Projects</BreadcrumbPage>
                        </BreadcrumbItem>
                      </Link>

                      {locationTokens.length > 1 && (
                        <>
                          <BreadcrumbSeparator />

                          {/* Add project */}
                          {locationTokens.length === 2 &&
                          locationTokens[1] === "add" ? (
                            <BreadcrumbItem>
                              <BreadcrumbPage>New project</BreadcrumbPage>
                            </BreadcrumbItem>
                          ) : (
                            <>
                              {/* Project name */}
                              <BreadcrumbItem>
                                <BreadcrumbPage>
                                  {fetchingProject ? (
                                    <>
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    </>
                                  ) : (
                                    <>
                                      <span>{projectName}</span>
                                    </>
                                  )}
                                </BreadcrumbPage>
                              </BreadcrumbItem>

                              <BreadcrumbSeparator />

                              {/* Edit project */}
                              {locationTokens[2] === "edit" && (
                                <BreadcrumbItem>
                                  <BreadcrumbPage>Edit project</BreadcrumbPage>
                                </BreadcrumbItem>
                              )}

                              {/* Project properties */}
                              {locationTokens[2] === "properties" && (
                                <BreadcrumbItem>
                                  <BreadcrumbPage>Properties</BreadcrumbPage>
                                </BreadcrumbItem>
                              )}

                              {/* Project context */}
                              {locationTokens[2] === "context" && (
                                <BreadcrumbItem>
                                  <BreadcrumbPage>Context</BreadcrumbPage>
                                </BreadcrumbItem>
                              )}

                              {/* Project access */}
                              {locationTokens[2] === "access" && (
                                <>
                                  <BreadcrumbItem>
                                    <BreadcrumbPage>Access</BreadcrumbPage>
                                  </BreadcrumbItem>

                                  <BreadcrumbSeparator />

                                  <BreadcrumbItem>
                                    <BreadcrumbPage>
                                      {locationTokens[3] === "members"
                                        ? "Members"
                                        : "Invites"}
                                    </BreadcrumbPage>
                                  </BreadcrumbItem>
                                </>
                              )}

                              {/* Hazards */}
                              {locationTokens[2] === "hazards" && (
                                <>
                                  <Link to={`/projects/${projectId}/hazards`}>
                                    <BreadcrumbItem>
                                      <BreadcrumbPage>Hazards</BreadcrumbPage>
                                    </BreadcrumbItem>
                                  </Link>

                                  {locationTokens.length > 3 && (
                                    <>
                                      {/* Add hazard */}
                                      {locationTokens[3] === "add" && (
                                        <>
                                          <BreadcrumbSeparator />

                                          <BreadcrumbItem>
                                            <BreadcrumbPage>
                                              New hazard
                                            </BreadcrumbPage>
                                          </BreadcrumbItem>
                                        </>
                                      )}

                                      {/* Edit hazard */}
                                      {locationTokens[4] === "edit" && (
                                        <>
                                          <BreadcrumbSeparator />

                                          {/* Hazard name */}
                                          <BreadcrumbItem>
                                            <BreadcrumbPage>
                                              {fetchingHazard ? (
                                                <>
                                                  <Loader2 className="h-4 w-4 animate-spin" />
                                                </>
                                              ) : (
                                                <>
                                                  <span>
                                                    {hazardIdentifier}
                                                  </span>
                                                </>
                                              )}
                                            </BreadcrumbPage>
                                          </BreadcrumbItem>

                                          <BreadcrumbSeparator />

                                          <BreadcrumbItem>
                                            <BreadcrumbPage>
                                              Edit hazard
                                            </BreadcrumbPage>
                                          </BreadcrumbItem>
                                        </>
                                      )}
                                    </>
                                  )}
                                </>
                              )}
                            </>
                          )}
                        </>
                      )}
                    </>
                  )}

                  {/* Admin Portal */}
                  {locationTokens[0] === "admin-portal" && (
                    <>
                      <Link to="/admin-portal">
                        <BreadcrumbItem>
                          <BreadcrumbPage>Admin Portal</BreadcrumbPage>
                        </BreadcrumbItem>
                      </Link>

                      <BreadcrumbSeparator />

                      <BreadcrumbItem>
                        <BreadcrumbPage>
                          {locationTokens[1] === "users"
                            ? "User Management"
                            : "Application Invites"}
                        </BreadcrumbPage>
                      </BreadcrumbItem>
                    </>
                  )}
                </BreadcrumbList>
              </Breadcrumb>
            </div>
          </header>

          <main className="flex-grow container mx-auto px-4 py-2">
            <Outlet />
          </main>

          <footer className="bg-gray-200 text-center py-3">
            <p className="text-gray-600">
              &copy; 2025 <strong>AI Safety Log</strong> by{" "}
              <strong>Allayze Pty Ltd</strong>
            </p>
          </footer>
        </SidebarInset>
      </SidebarProvider>
    </div>
  );
};

export default AppLayout;
